import * as Sentry from '@sentry/react';
import { machineIdState } from '../../api/stores/shared';
import type { GateIssue } from './categorizeGates';
import {
  isNonsoleReadinessServiceError,
  type NonsoleReadinessServiceErrorCode,
} from '../../services/nonsoleReadiness/nonsoleReadinessErrors';

const HOST_MODE_SENTRY_MESSAGE_PREFIX = '[HostMode]';

/** Known contact-support gate IDs (mirrors categorizeGates CONTACT_SUPPORT_IDS). */
export const CONTACT_SUPPORT_GATE_IDS = [
  'ROLLOUT_ENABLED',
  'SESSION_RETENTION_POLICY',
  'PERSISTENT_ACCOUNT',
  'HOST_EXE',
  'INPUT_DRIVERS_HEALTHY',
  'PLAYCAST_DRIVERS_HEALTHY',
  'SECURITY_SIGNATURE',
  'KERNEL_DEBUGGER',
  'VM_ENVIRONMENT',
] as const;

export type ContactSupportGateId = (typeof CONTACT_SUPPORT_GATE_IDS)[number];

const GATE_ID_TO_ERROR_CODE: Record<ContactSupportGateId, HostModeGateErrorCode> = {
  ROLLOUT_ENABLED: 'HM_GATE_ROLLOUT_ENABLED',
  SESSION_RETENTION_POLICY: 'HM_GATE_SESSION_RETENTION_POLICY',
  PERSISTENT_ACCOUNT: 'HM_GATE_PERSISTENT_ACCOUNT',
  HOST_EXE: 'HM_GATE_HOST_EXE',
  INPUT_DRIVERS_HEALTHY: 'HM_GATE_INPUT_DRIVERS_HEALTHY',
  PLAYCAST_DRIVERS_HEALTHY: 'HM_GATE_PLAYCAST_DRIVERS_HEALTHY',
  SECURITY_SIGNATURE: 'HM_GATE_SECURITY_SIGNATURE',
  KERNEL_DEBUGGER: 'HM_GATE_KERNEL_DEBUGGER',
  VM_ENVIRONMENT: 'HM_GATE_VM_ENVIRONMENT',
};

export const HOST_MODE_TRANSPORT_ERROR_CODES = [
  'HM_REPORT_TIMEOUT',
  'HM_REPORT_FAILED',
  'HM_REPORT_MALFORMED',
  'HM_ENTRY_TIMEOUT',
  'HM_ENTRY_MALFORMED',
  'HM_SERVICE_DESTROYED',
  'HM_TRANSPORT',
] as const;

export type HostModeTransportErrorCode = (typeof HOST_MODE_TRANSPORT_ERROR_CODES)[number];

export const HOST_MODE_NATIVE_ERROR_CODES = [
  'HM_BUSY',
  'HM_NOT_IN_NORMAL_STATE',
  'HM_HOST_MODE_DISABLED',
  'HM_CANCELLED_BY_TIMEOUT',
  'HM_EVALUATION_FAILED',
  'HM_UNKNOWN',
] as const;

export type HostModeNativeErrorCode = (typeof HOST_MODE_NATIVE_ERROR_CODES)[number];

export const HOST_MODE_GATE_ERROR_CODES = [
  'HM_GATE_ROLLOUT_ENABLED',
  'HM_GATE_SESSION_RETENTION_POLICY',
  'HM_GATE_PERSISTENT_ACCOUNT',
  'HM_GATE_HOST_EXE',
  'HM_GATE_INPUT_DRIVERS_HEALTHY',
  'HM_GATE_PLAYCAST_DRIVERS_HEALTHY',
  'HM_GATE_SECURITY_SIGNATURE',
  'HM_GATE_KERNEL_DEBUGGER',
  'HM_GATE_VM_ENVIRONMENT',
  'HM_GATE_UNMAPPED',
] as const;

export type HostModeGateErrorCode = (typeof HOST_MODE_GATE_ERROR_CODES)[number];

export const HOST_MODE_CONTROLLER_ERROR_CODES = ['HM_ENTRY_LOOP'] as const;

export type HostModeControllerErrorCode = (typeof HOST_MODE_CONTROLLER_ERROR_CODES)[number];

export type HostModeErrorCode =
  | HostModeTransportErrorCode
  | HostModeNativeErrorCode
  | HostModeGateErrorCode
  | HostModeControllerErrorCode;

const SERVICE_CODE_TO_HOST_MODE: Record<NonsoleReadinessServiceErrorCode, HostModeTransportErrorCode> = {
  report_timeout: 'HM_REPORT_TIMEOUT',
  report_failed: 'HM_REPORT_FAILED',
  report_malformed: 'HM_REPORT_MALFORMED',
  entry_timeout: 'HM_ENTRY_TIMEOUT',
  entry_malformed: 'HM_ENTRY_MALFORMED',
  service_destroyed: 'HM_SERVICE_DESTROYED',
};

const NATIVE_ERROR_TO_CODE: Record<string, HostModeNativeErrorCode> = {
  busy: 'HM_BUSY',
  not_in_normal_state: 'HM_NOT_IN_NORMAL_STATE',
  host_mode_disabled: 'HM_HOST_MODE_DISABLED',
  cancelled_by_timeout: 'HM_CANCELLED_BY_TIMEOUT',
  timeout: 'HM_CANCELLED_BY_TIMEOUT',
  evaluation_failed: 'HM_EVALUATION_FAILED',
};

/** Native wire tokens safe to attach to Sentry `extra` (no free-form diagnostic prose). */
const ALLOWED_NATIVE_ERROR_TOKENS = new Set<string>([
  ...Object.keys(NATIVE_ERROR_TO_CODE),
  'gates_failed',
  'unknown',
]);

/**
 * Returns the native error token for Sentry only when it is on the allowlist.
 * Unlisted values are omitted so arbitrary native text never reaches Sentry extra.
 */
export function nativeErrorTokenForSentry(nativeError?: string): string | undefined {
  if (nativeError === undefined) {
    return undefined;
  }
  const trimmed = nativeError.trim();
  if (trimmed.length === 0 || !ALLOWED_NATIVE_ERROR_TOKENS.has(trimmed)) {
    return undefined;
  }
  return trimmed;
}

const THROTTLE_MS = 60 * 60 * 1000;
const lastSentMap = new Map<string, number>();

export function shouldThrottleHostModeError(code: HostModeErrorCode, machineId: string): boolean {
  const key = `${code}:${machineId}`;
  const now = Date.now();
  const lastSent = lastSentMap.get(key);
  if (lastSent !== undefined && now - lastSent < THROTTLE_MS) {
    return true;
  }
  lastSentMap.set(key, now);
  return false;
}

/** Clears throttle state — use in test teardown. */
export function resetHostModeErrorThrottle(): void {
  lastSentMap.clear();
}

export function hostModeErrorCodeFromNativeError(nativeError: string): HostModeErrorCode {
  const trimmed = nativeError.trim();
  const mapped = NATIVE_ERROR_TO_CODE[trimmed];
  return mapped ?? 'HM_UNKNOWN';
}

export function hostModeErrorCodeFromGateId(gateId: string): HostModeErrorCode {
  if (gateId === 'ENTRY_LOOP') {
    return 'HM_ENTRY_LOOP';
  }
  const known = GATE_ID_TO_ERROR_CODE[gateId as ContactSupportGateId];
  return known ?? 'HM_GATE_UNMAPPED';
}

export function hostModeErrorCodeFromGates(contactSupport: GateIssue[]): HostModeErrorCode {
  const first = contactSupport[0];
  if (!first) {
    return 'HM_GATE_UNMAPPED';
  }
  if (first.gateId === 'UNKNOWN') {
    return hostModeErrorCodeFromNativeError(first.detail);
  }
  return hostModeErrorCodeFromGateId(first.gateId);
}

export function hostModeErrorCodeFromThrow(err: unknown): HostModeErrorCode {
  if (isNonsoleReadinessServiceError(err)) {
    return SERVICE_CODE_TO_HOST_MODE[err.serviceCode];
  }
  if (err instanceof Error) {
    if (err.message.includes('Timeout waiting for nonsole readiness report')) {
      return 'HM_REPORT_TIMEOUT';
    }
    if (err.message.includes('Timeout waiting for host mode entry result')) {
      return 'HM_ENTRY_TIMEOUT';
    }
    if (err.message === 'Nonsole readiness report failed') {
      return 'HM_REPORT_FAILED';
    }
    if (err.message === 'Malformed readiness report in accepted entry') {
      return 'HM_ENTRY_MALFORMED';
    }
    if (err.message.startsWith('Malformed readiness report')) {
      return 'HM_REPORT_MALFORMED';
    }
    if (err.message === 'Service destroyed') {
      return 'HM_SERVICE_DESTROYED';
    }
  }
  return 'HM_TRANSPORT';
}

export function formatHostModeSoftFailMessage(code: HostModeErrorCode): string {
  return `Couldn't check readiness — try again (${code})`;
}

export interface HostModeFailureReportParams {
  code: HostModeErrorCode;
  phase: string;
  gateIds?: string[];
  nativeError?: string;
  durationMs?: number;
}

export function reportHostModeFailure(params: HostModeFailureReportParams): void {
  const machineId = machineIdState.currentState || 'unknown';
  if (shouldThrottleHostModeError(params.code, machineId)) {
    return;
  }

  const errorForSentry = new Error(`${HOST_MODE_SENTRY_MESSAGE_PREFIX} ${params.code}`);

  const nativeErrorToken = nativeErrorTokenForSentry(params.nativeError);

  Sentry.captureException(errorForSentry, {
    tags: { hostModeErrorCode: params.code },
    extra: {
      code: params.code,
      phase: params.phase,
      gateIds: params.gateIds,
      ...(nativeErrorToken !== undefined ? { nativeErrorToken } : {}),
      machineId,
      durationMs: params.durationMs,
    },
  });
}
